interface RuntimeConfig {
    VITE_OKTA_ENV: string;
    VITE_OKTA_CLIENT_ID: string;
    VITE_OKTA_ISSUER: string;
    VITE_API_HOST_VALUE: string;
    VITE_SENTRY_ENABLED: string;
}

let cachedConfig: RuntimeConfig | null = null;

/**
 * Loads runtime configuration.
 * - Development: Uses environment variables from Aspire/Vite
 * - Production: Fetches config.json deployed by CDK
 */
export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
    // Return cached config if already loaded
    if (cachedConfig) {
        return cachedConfig;
    }

    console.log('[RuntimeConfig] Loading configuration');
    console.log('[RuntimeConfig] MODE:', import.meta.env.MODE);

    const isDevelopment = import.meta.env.MODE === 'development';

    let config: RuntimeConfig;

    if (isDevelopment) {
        // Development: Use environment variables (from Aspire or .env files)
        console.log('[RuntimeConfig] Development mode - using environment variables');
        config = {
            VITE_OKTA_ENV: import.meta.env.VITE_OKTA_ENV || '',
            VITE_OKTA_CLIENT_ID: import.meta.env.VITE_OKTA_CLIENT_ID || '',
            VITE_OKTA_ISSUER: import.meta.env.VITE_OKTA_ISSUER || '',
            VITE_API_HOST_VALUE: import.meta.env.VITE_API_HOST_VALUE || '',
            VITE_SENTRY_ENABLED: import.meta.env.VITE_SENTRY_ENABLED || 'false',
        };
    } else {
        // Production: Fetch config.json deployed by CDK
        console.log('[RuntimeConfig] Production mode - fetching config.json');
        try {
            const response = await fetch('/config.json');
            if (!response.ok) {
                throw new Error(`Failed to load config.json: ${response.statusText}`);
            }
            config = await response.json();
            console.log('[RuntimeConfig] Successfully loaded config.json');
        } catch (error) {
            console.error('[RuntimeConfig] Failed to load config.json:', error);
            // Fallback to environment variables (might be empty in production)
            config = {
                VITE_OKTA_ENV: import.meta.env.VITE_OKTA_ENV || '',
                VITE_OKTA_CLIENT_ID: import.meta.env.VITE_OKTA_CLIENT_ID || '',
                VITE_OKTA_ISSUER: import.meta.env.VITE_OKTA_ISSUER || '',
                VITE_API_HOST_VALUE: import.meta.env.VITE_API_HOST_VALUE || '',
                VITE_SENTRY_ENABLED: import.meta.env.VITE_SENTRY_ENABLED || 'false',
            };
        }
    }

    console.log('[RuntimeConfig] Configuration loaded:', {
        hasIssuer: !!config.VITE_OKTA_ISSUER,
        hasClientId: !!config.VITE_OKTA_CLIENT_ID,
        hasApiHost: !!config.VITE_API_HOST_VALUE,
        issuer: config.VITE_OKTA_ISSUER,
        clientId: config.VITE_OKTA_CLIENT_ID,
        apiHost: config.VITE_API_HOST_VALUE,
    });

    // Validate required fields
    if (!config.VITE_OKTA_ISSUER || !config.VITE_OKTA_CLIENT_ID) {
        const errorMsg =
            'Runtime configuration is missing required Okta settings. ' +
            'Ensure environment variables are properly set (dev) or config.json exists (production). ' +
            `Missing: ${!config.VITE_OKTA_ISSUER ? 'VITE_OKTA_ISSUER ' : ''}${!config.VITE_OKTA_CLIENT_ID ? 'VITE_OKTA_CLIENT_ID' : ''}`;
        console.error('[RuntimeConfig]', errorMsg);
        throw new Error(errorMsg);
    }

    if (!config.VITE_API_HOST_VALUE) {
        console.warn('[RuntimeConfig] VITE_API_HOST_VALUE is not set - API calls may fail');
    }

    cachedConfig = config;
    return config;
}

/**
 * Gets the cached config (synchronous)
 * Throws an error if config hasn't been loaded yet
 */
export function getConfig(): RuntimeConfig {
    if (!cachedConfig) {
        throw new Error(
            'Configuration has not been loaded yet. ' +
            'Call await loadRuntimeConfig() before accessing config.'
        );
    }
    return cachedConfig;
}